// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/ResearchCard.tsx (the findings rows)
// ADAPTED (plan K0/K1): Brand profile and Best sellers only, from the brand guide; TP's Reddit and Reviews are not ported.
import { ChevronDown, ImageOff, Palette, Tag, type LucideIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { BrandLogo } from '@/components/ui/brand-logo';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { BrandGuideResponse } from '@/contracts/onboarding-session';
import { cn } from '@/lib/utils';

import { countLabel, LOGO_LABEL, productsLabel, productsSummary } from '../copy';
import { bestSellersOf, profileOf, prospectLogoUrl } from '../state/researchSteps';
import { SiteImage } from './SiteImage';

export const Dot = () => (
  <span aria-hidden className="text-ds-text-3">
    ·
  </span>
);

/**
 * What the research found, as rows under the card's steps: the brand profile and the best sellers, open as they land
 * and folded once the research is done (the person can open them again). A part the guide has not got is left out.
 */
export function ResearchFindings({ brandGuide, brand, site, done }: { brandGuide: BrandGuideResponse | null; brand: string; site: string; done: boolean }) {
  const profile = profileOf(brandGuide);
  const sellers = bestSellersOf(brandGuide);
  if (!profile && sellers.length === 0) return null;
  const logo = prospectLogoUrl(brandGuide);
  const productsName = productsLabel(brandGuide?.productsSource);
  const profileBits: ReactNode[] = profile
    ? [
        profile.font ? <span key="font">{profile.font}</span> : null,
        profile.colors.length > 0 ? (
          <span key="colors" className="flex items-center gap-0.5" role="img" aria-label={`${profile.colors.length} brand ${profile.colors.length === 1 ? 'colour' : 'colours'}`}>
            {profile.colors.map((c) => (
              <span key={c} className="size-3.5 rounded-full border border-ds-hairline" style={{ background: c }} />
            ))}
          </span>
        ) : null,
        profile.quote ? (
          <span key="quote" className="min-w-0 truncate text-ds-text-2">
            {`“${profile.quote}”`}
          </span>
        ) : null,
      ].filter(Boolean)
    : [];
  return (
    <div className="divide-y divide-ds-hairline border-t border-ds-hairline">
      {profile ? (
        <Finding
          id="profile"
          icon={Palette}
          title="Brand profile"
          summary={
            [
              profile.font,
              profile.colors.length > 0 ? countLabel(profile.colors.length, 'color', 'colors') : null,
              profile.hasVoice ? 'voice' : null,
            ]
              .filter(Boolean)
              .join(' · ') || LOGO_LABEL
          }
          done={done}
        >
          <div className="flex flex-wrap items-center gap-2 text-body-sm text-foreground">
            <BrandLogo name={brand} src={logo} className="size-5" />
            {/* The brand's name is the summary line right above the card's rows, so it is not repeated here. */}
            {profileBits.flatMap((bit, i) => [i > 0 ? <Dot key={`dot-${i}`} /> : null, bit])}
          </div>
        </Finding>
      ) : null}
      {sellers.length > 0 ? (
        <Finding id="bestSellers" icon={Tag} title={productsName} summary={productsSummary(brandGuide?.productsSource, sellers.length, site)} done={done}>
          <ul className="flex gap-1.5 overflow-x-auto" aria-label={productsName}>
            {sellers.map((p, i) => (
              <li key={`${i}:${p.name}`} className="shrink-0">
                <SiteImage
                  src={p.imageUrl}
                  alt={p.name}
                  title={p.name}
                  className="h-20 w-14 rounded-sm object-cover object-top"
                  fallback={
                    <span role="img" aria-label={p.name} title={p.name} className="flex h-20 w-14 items-center justify-center rounded-sm bg-ds-ink-a05 text-ds-text-3">
                      <ImageOff aria-hidden className="size-4" />
                    </span>
                  }
                />
              </li>
            ))}
          </ul>
        </Finding>
      ) : null}
    </div>
  );
}

/** One thing the research found, as a row: open as it lands, folded once the research is done (the person can open it again). */
function Finding({
  id,
  icon: Icon,
  title,
  summary,
  done,
  children,
}: {
  id: 'profile' | 'bestSellers';
  icon: LucideIcon;
  title: string;
  summary: string;
  done: boolean;
  children: ReactNode;
}) {
  const [isOpen, setOpen] = useState(!done);
  // TP's rule, kept as is: only the move to done folds it, so a row opened afterwards stays open.
  useEffect(() => {
    if (done) setOpen(false);
  }, [done]);
  return (
    <Collapsible open={isOpen} onOpenChange={setOpen} data-finding={id}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors animate-in fade-in slide-in-from-bottom-1 duration-300 hover:bg-ds-ink-a05 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-focus motion-reduce:animate-none"
        >
          <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-sm bg-ds-ink-a05 text-ds-text-2">
            <Icon className="size-3.5" />
          </span>
          <span className="text-tab text-foreground">{title}</span>
          <span className="min-w-0 flex-1 truncate text-body-sm text-ds-text-2">{summary}</span>
          <ChevronDown aria-hidden className={cn('size-4 shrink-0 text-ds-text-2 transition-transform', isOpen && 'rotate-180')} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-[22px] border-l border-ds-hairline py-1 pb-3 pl-4 pr-3">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}
